import { StatusPill } from '@pages/User/components/StatusPill';
import { isBlockInactive } from '@root/helpers/blocks';
import type { CellContext, ColumnHelper, ColumnMeta } from '@tanstack/react-table';

interface Entry {
  deleted_at?: string | null;
  inception_date?: string | null;
  expiry_date?: string | null;
}

enum Status {
  Active = 'Active',
  Archived = 'Archived',
  Inactive = 'Inactive',
}

const getStatus = <T extends Entry>(item: T): Status => {
  if (item.deleted_at) {
    return Status.Archived;
  }

  if (isBlockInactive(item.inception_date, item.expiry_date)) {
    return Status.Inactive;
  }

  return Status.Active;
};

export const statusAccessor = <T extends Entry>(
  columnHelper: ColumnHelper<T>,
  filterMeta: ColumnMeta<T, string>['filter'] = {},
) =>
  columnHelper.accessor((row) => row.deleted_at, {
    header: 'Status',
    id: 'deleted_at',
    size: 128,
    minSize: 128,
    maxSize: 128,
    filterFn: (row, _, allowedStatuses) => {
      const { deleted_at, inception_date, expiry_date } = row.original;

      if (!allowedStatuses.length) {
        return true;
      }

      const isArchived = !!deleted_at;
      const isInactive = isBlockInactive(inception_date, expiry_date);

      return allowedStatuses.includes(isArchived ? Status.Archived : isInactive ? Status.Inactive : Status.Active);
    },
    cell: (cellContext: CellContext<T, string>) => {
      const { deleted_at, inception_date, expiry_date } = cellContext.row.original;
      const isInactive = isBlockInactive(inception_date, expiry_date);
      return (
        <StatusPill
          content={deleted_at ? Status.Archived : isInactive ? Status.Inactive : Status.Active}
          variant={deleted_at ? 'archived' : isInactive ? 'inactive' : 'active'}
        />
      );
    },
    sortingFn: (a, b) => {
      const aStatus = getStatus(a.original);
      const bStatus = getStatus(b.original);

      // Alphabetical sort based on Status string values
      return aStatus.localeCompare(bStatus);
    },
    sortUndefined: false,
    meta: {
      filter: {
        type: 'dropdown',
        label: 'Status',
        options: [Status.Active, Status.Inactive, Status.Archived],
        ...filterMeta,
      },
      headingCellClassName: 'w-32',
    },
  });
